import caseAtlantic from "@/assets/case-atlantic.png?format=webp";
import caseTinder from "@/assets/case-tinder.png?format=webp";
import caseBumble from "@/assets/case-bumble.png?format=webp";
import caseUberEats from "@/assets/case-uber-eats.png?format=webp";
import casePeroni from "@/assets/case-peroni.png?format=webp";
import caseRuinart from "@/assets/case-ruinart.png?format=webp";
import caseLeuchtturm from "@/assets/case-leuchtturm.png?format=webp";
import caseGuardianGlass from "@/assets/case-guardian-glass.png?format=webp";
import caseSato from "@/assets/case-sato.png?format=webp";
import caseRia from "@/assets/case-ria.png?format=webp";

export interface CaseStudyResult {
  value: string;
  label: string;
}

export type CaseStudyIndustry = "Luxury" | "B2B" | "Tech" | "Hospitality";

export interface CaseStudy {
  slug: string;
  title: string;
  sector: string;
  market: string;
  /** Top-level industry tag for filtering on the homepage. */
  industry?: CaseStudyIndustry;
  tagline: string;
  problem: string;
  approach: string;
  outcome: string | null;
  heroImage: string;
  metric?: string;
  metricLabel?: string;
  results?: CaseStudyResult[];
  resultLine?: string;
  // SEO fields - optional overrides for auto-generated values
  seoTitle?: string;
  seoDescription?: string;
}

export const caseStudies: CaseStudy[] = [
  {
    slug: "atlantic-airways-performance-marketing",
    title: "Atlantic Airways Performance Marketing",
    sector: "Airline",
    market: "Faroe Islands",
    industry: "Hospitality",
    tagline: "Handle ongoing performance marketing campaigns to drive demand and bookings for routes from London Gatwick, Barcelona, New York, and Paris to the Faroe Islands. Recorded video content and developed creative assets for brand repositioning.",
    problem: "Atlantic Airways needed to raise awareness of the Faroe Islands as a destination and increase direct flight bookings from key European and US markets.",
    approach: "The Zoom Agency handles Atlantic Airways' performance marketing, driving demand and bookings for key routes. The team also produced video content and developed creative assets as part of a brand repositioning effort.",
    outcome: "Increased visits to the Faroe Islands from the US, UK, Spain, and France.",
    heroImage: caseAtlantic,
    metric: "3×",
    metricLabel: "Booking increase",
    results: [
      { value: "+4", label: "New markets launched" },
      { value: "3x", label: "Booking increase" },
      { value: "2M+", label: "Campaign impressions" },
    ],
    resultLine: "Repositioned across 4 European and US markets",
    seoTitle: "Atlantic Airways Airline Case Study | The Zoom Agency",
    seoDescription: "Increased tourism to the Faroe Islands from US, UK, Spain and France. See how performance marketing drove flight bookings for this Nordic airline.",
  },
  {
    slug: "tinder-expansion-strategy",
    title: "Tinder European Expansion Strategy",
    sector: "Dating App",
    market: "Spain",
    industry: "Tech",
    tagline: "Instrumental in executing Tinder's early localised campaigns during Tinder Plus rollout in Spain. Spain became one of Tinder's fastest-growing markets, exceeding user acquisition targets through culturally-aware marketing strategies.",
    problem: "Tinder needed to localise its marketing to drive adoption of Tinder Plus in new European markets, starting with Spain.",
    approach: "The Zoom Agency executed Tinder's early localised campaigns for the Tinder Plus launch, employing culturally-aware marketing strategies tailored to the Spanish market.",
    outcome: "Spain became one of Tinder's fastest-growing markets, exceeding user acquisition targets through culturally-aware campaigns.",
    heroImage: caseTinder,
    metric: "#1",
    metricLabel: "Fastest-growing EU market",
    results: [
      { value: "#1", label: "Fastest-growing EU market" },
      { value: "150%", label: "Above acquisition target" },
    ],
    resultLine: "Spain became Tinder's fastest-growing European market",
    seoTitle: "Tinder European Expansion Case Study | The Zoom Agency",
    seoDescription: "Spain became Tinder's fastest-growing European market. See how localised campaigns exceeded user acquisition targets for this dating app launch.",
  },
  {
    slug: "bumble-spain-market-expansion",
    title: "Bumble Spain Market Expansion Strategy",
    sector: "Dating App",
    market: "Spain",
    industry: "Tech",
    tagline: "Delivered comprehensive PR, experiential and social strategy for Bumble's expansion into the Spanish market. Created innovative events like 'Speed Dating Meets Spin Class' and secured major press coverage cementing brand reputation.",
    problem: "Bumble needed to build brand awareness and user adoption in the Spanish dating app market.",
    approach: "The Zoom Agency delivered an integrated PR, experiential, and social media strategy for Bumble's Spain launch, creating innovative events like 'Speed Dating Meets Spin Class' and securing major press coverage.",
    outcome: null,
    heroImage: caseBumble,
    seoTitle: "Bumble Spain Launch Case Study | The Zoom Agency",
    seoDescription: "Major press coverage and innovative experiential events drove Bumble's Spain market entry. PR, social and event strategy case study.",
  },
  {
    slug: "uber-eats-barcelona-launch",
    title: "Uber Eats Barcelona Market Launch Strategy",
    sector: "Food Delivery App",
    market: "Spain",
    industry: "Tech",
    tagline: "Launched Uber Eats in Barcelona using strategic social media influencer and creator campaigns. Developed comprehensive go-to-market strategy that established strong brand presence in the competitive Spanish food delivery market.",
    problem: "Uber wanted a successful launch of Uber Eats in Barcelona's competitive food delivery market.",
    approach: "The Zoom Agency orchestrated Uber Eats' Barcelona launch through strategic influencer and creator campaigns, developing a comprehensive go-to-market strategy to establish the brand locally.",
    outcome: null,
    heroImage: caseUberEats,
    seoTitle: "Uber Eats Barcelona Launch Case Study | The Zoom Agency",
    seoDescription: "Influencer campaigns and go-to-market strategy established Uber Eats in Barcelona's competitive food delivery market. Launch case study.",
  },
  {
    slug: "peroni-brand-activation",
    title: "Peroni Nastro Azzurro Brand Activation",
    sector: "Beverage (Beer)",
    market: "Spain",
    industry: "Hospitality",
    tagline: "Developed integrated marketing campaigns for Peroni Nastro Azzurro focusing on lifestyle positioning and cultural connections. Created engaging content strategies that celebrated Italian heritage while building modern brand relevance.",
    problem: "Peroni Nastro Azzurro sought to strengthen its lifestyle brand image and cultural relevance to engage consumers.",
    approach: "The Zoom Agency developed integrated campaigns for Peroni focused on lifestyle positioning and cultural connections, creating engaging content and on-ground brand experiences.",
    outcome: null,
    heroImage: casePeroni,
    seoTitle: "Peroni Nastro Azzurro Brand Case Study | The Zoom Agency",
    seoDescription: "Lifestyle positioning and cultural content strategy strengthened Peroni's premium beer brand in Spain. Brand activation case study.",
  },
  {
    slug: "ruinart-sensoria",
    title: "Ruinart Rendezvous & Experiential Events",
    sector: "Luxury Wine (Champagne)",
    market: "France",
    industry: "Luxury",
    tagline: "Managed ongoing Ruinart Rendezvous events and conceived Ruinart Sensoria - bringing influencers and creators to Maison Ruinart in France for multi-sensory experiences. Generated extensive organic social content and brand repositioning.",
    problem: "Ruinart wanted to modernise its brand marketing and engage a new generation of luxury consumers through experiential campaigns.",
    approach: "The Zoom Agency managed the ongoing 'Ruinart Rendezvous' series and created the 'Ruinart Sensoria' experience, inviting key influencers to Maison Ruinart for immersive, multi-sensory brand events.",
    outcome: "Generated extensive organic social content and buzz around the Ruinart brand.",
    heroImage: caseRuinart,
    seoTitle: "Ruinart Champagne Experiential Case Study | The Zoom Agency",
    seoDescription: "Extensive organic social content from influencer experiences at Maison Ruinart. Luxury champagne brand experiential marketing case study.",
  },
  {
    slug: "leuchtturm1917-community-building",
    title: "Leuchtturm1917 Community Building & Growth",
    sector: "Stationery",
    market: "Global",
    industry: "Luxury",
    tagline: "Grew Instagram following from 0 to over 200,000 highly engaged followers. Built a passionate community that influenced product development and helped compete with Moleskine through strategic content creation.",
    problem: "Leuchtturm1917 needed to build a strong social media presence from scratch to compete with established players like Moleskine.",
    approach: "The Zoom Agency developed and executed a long-term social media strategy focused on community building, user-generated content, and strategic partnerships with bullet journaling communities.",
    outcome: "Grew Instagram following from 0 to over 200,000 highly engaged followers. Built a community that influenced product development.",
    heroImage: caseLeuchtturm,
    metric: "200K+",
    metricLabel: "Followers from zero",
    results: [
      { value: "200K+", label: "Followers from zero" },
      { value: "Long-term", label: "Partnership" },
      { value: "Top 3", label: "Competitor position vs Moleskine" },
    ],
    seoTitle: "Leuchtturm1917 Social Media Case Study | The Zoom Agency",
    seoDescription: "Grew Instagram from 0 to 200,000+ engaged followers. Community building strategy helped this stationery brand compete with Moleskine.",
  },
  {
    slug: "guardian-glass-demand-generation",
    title: "Guardian Glass Consumer Demand Generation",
    sector: "B2B Manufacturing",
    market: "Europe",
    industry: "B2B",
    tagline: "Created 'The Great Outdoors' campaign to generate consumer-level demand for specialty glass, driving B2B sales through dealers. Dealers reported customers 'asking for Guardian glass by name' after our comprehensive strategy execution.",
    problem: "Guardian Glass needed to generate consumer-level awareness and demand for their specialty glass products to drive B2B sales through dealer networks.",
    approach: "The Zoom Agency created 'The Great Outdoors' campaign, developing consumer-facing content and dealer support materials to generate pull-through demand.",
    outcome: "Dealers reported customers 'asking for Guardian glass by name' after the campaign execution.",
    heroImage: caseGuardianGlass,
    resultLine: "Dealers reported customers asking for Guardian Glass by name",
    seoTitle: "Guardian Glass B2B Demand Gen Case Study | The Zoom Agency",
    seoDescription: "Customers asked for Guardian Glass by name after our campaign. B2B demand generation through consumer marketing case study.",
  },
  {
    slug: "sato-europe-agency-of-record",
    title: "SATO Europe Agency of Record",
    sector: "B2B Technology",
    market: "Europe",
    industry: "B2B",
    tagline: "Ongoing agency of record for SATO Europe. Built European brand identity from fragmented regional activity, delivering comprehensive campaigns in 8 languages with measurable growth in visibility and partner engagement.",
    problem: "SATO Europe had fragmented regional marketing activity and needed to build a cohesive European brand identity.",
    approach: "As agency of record, The Zoom Agency unified SATO's European marketing, delivering comprehensive campaigns across 8 languages and establishing consistent brand positioning.",
    outcome: "Achieved measurable growth in brand visibility and partner engagement across European markets.",
    heroImage: caseSato,
    metric: "8",
    metricLabel: "Languages delivered",
    results: [
      { value: "8", label: "Languages delivered" },
      { value: "1", label: "Unified European brand" },
    ],
    seoTitle: "SATO Europe B2B Technology Case Study | The Zoom Agency",
    seoDescription: "Unified European brand across 8 languages with measurable growth. B2B technology agency of record case study.",
  },
  {
    slug: "ria-money-transfer-campaign",
    title: "Ria Money Transfer Social Refresh Campaign",
    sector: "Financial Services",
    market: "Diaspora Markets",
    industry: "Tech",
    tagline: "Refreshed Ria's social presence and ad campaigns in diaspora markets through authentic testimonial videos and challenger-brand messaging. Increased app downloads and transaction volume while building trusted social channels.",
    problem: "Ria Money Transfer needed to refresh their social media presence and build trust in diaspora markets.",
    approach: "The Zoom Agency developed authentic testimonial video campaigns and challenger-brand messaging tailored to diaspora communities, refreshing Ria's social presence across key markets.",
    outcome: "Increased app downloads and transaction volume while building trusted social channels.",
    heroImage: caseRia,
    seoTitle: "Ria Money Transfer Fintech Case Study | The Zoom Agency",
    seoDescription: "Increased app downloads and transaction volume in diaspora markets. Authentic testimonial campaigns built trust for this money transfer brand.",
  },
];

// Helper to get featured case studies for homepage
export const getFeaturedCaseStudies = (): CaseStudy[] => {
  const featuredSlugs = [
    "atlantic-airways-performance-marketing",
    "tinder-expansion-strategy",
    "guardian-glass-demand-generation",
  ];
  return featuredSlugs
    .map((slug) => caseStudies.find((cs) => cs.slug === slug))
    .filter((cs): cs is CaseStudy => cs !== undefined);
};

// Helper to get a case study by slug
export const getCaseStudyBySlug = (slug: string): CaseStudy | undefined => {
  return caseStudies.find((cs) => cs.slug === slug);
};
